import { useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { FolderOpen, Trash2, X, CheckSquare, Archive } from "lucide-react";

export const useMultiSelect = () => {
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [selectMode, setSelectMode] = useState(false);

  const toggle = useCallback((id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const selectAll = useCallback((ids) => setSelectedIds(new Set(ids)), []);
  const clearAll = useCallback(() => { setSelectedIds(new Set()); setSelectMode(false); }, []);
  const isSelected = useCallback((id) => selectedIds.has(id), [selectedIds]);

  return { selectedIds, selectMode, setSelectMode, toggle, selectAll, clearAll, isSelected, count: selectedIds.size };
};

export const BulkActionBar = ({ count, folders, onBulkMove, onBulkDelete, onBulkArchive, onClear }) => {
  const [moveTarget, setMoveTarget] = useState(null);

  if (count === 0) return null;

  return (
    <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 bg-card border border-border rounded-lg shadow-xl px-4 py-2 flex items-center gap-3 flex-wrap" data-testid="bulk-action-bar">
      <span className="text-sm font-medium text-foreground" data-testid="bulk-action-count">{count} selected</span>
      <div className="w-px h-5 bg-border" />
      {folders && folders.length > 0 && (
        <div className="flex items-center gap-1">
          <select className="h-7 text-xs bg-input border border-border rounded-sm px-2 text-foreground" value={moveTarget || ""} onChange={e => setMoveTarget(e.target.value || null)} data-testid="bulk-folder-select">
            <option value="">Move to folder...</option>
            <option value="__none__">Unfiled</option>
            {folders.filter(f => !f.archived).map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
          </select>
          <Button size="sm" variant="outline" className="h-7 text-xs rounded-sm" disabled={!moveTarget} onClick={() => { onBulkMove(moveTarget === "__none__" ? null : moveTarget); setMoveTarget(null); }} data-testid="bulk-move-btn">
            <FolderOpen className="w-3 h-3 mr-1" /> Move
          </Button>
        </div>
      )}
      {onBulkArchive && (
        <Button size="sm" variant="outline" className="h-7 text-xs rounded-sm" onClick={onBulkArchive} data-testid="bulk-archive-btn">
          <Archive className="w-3 h-3 mr-1" /> Archive
        </Button>
      )}
      <Button size="sm" variant="destructive" className="h-7 text-xs rounded-sm" onClick={onBulkDelete} data-testid="bulk-delete-btn">
        <Trash2 className="w-3 h-3 mr-1" /> Delete
      </Button>
      <button className="text-muted-foreground hover:text-foreground p-1" onClick={onClear} data-testid="bulk-clear-btn"><X className="w-4 h-4" /></button>
    </div>
  );
};

export const SelectCheckbox = ({ checked, onChange }) => (
  <div className="absolute top-2 left-2 z-10" onClick={e => { e.stopPropagation(); onChange(); }}>
    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center cursor-pointer transition-colors ${checked ? "bg-primary border-primary" : "border-muted-foreground/40 hover:border-primary/60 bg-card/80"}`} data-testid="select-checkbox">
      {checked && <CheckSquare className="w-3.5 h-3.5 text-white" />}
    </div>
  </div>
);
